import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment-timezone'
import { Button, Card, CardBody, CardHeader, Checkbox, Text, Tooltip, TooltipTrigger, TooltipContent } from '@evertel/web/ui'
import { DepartmentAvatar } from '@evertel/web/department'
import { AgreementTextEditor } from './AgreementTextEditor'

interface AgreementGateProps {
    content: string
    roomName?: string
    roomDescription?: string
    departmentId?: number
    departmentName?: string
    agreementName?: string
    effectiveDate?: Date | string
    version?: number
    currentStep?: number
    totalSteps?: number
    isAccepting?: boolean
    onAccept: () => void
    onBack?: () => void
}

const AgreementGate: React.FC<AgreementGateProps> = observer(({
    content,
    roomName = 'Room Name',
    roomDescription,
    departmentId,
    departmentName,
    agreementName,
    effectiveDate,
    version,
    currentStep,
    totalSteps,
    isAccepting = false,
    onAccept,
    onBack
}) => {
    const [isAgreed, setIsAgreed] = useState(false)
    const [readToEnd, setReadToEnd] = useState(false)
    const showSteps = currentStep !== undefined && totalSteps !== undefined && totalSteps > 1

    return (
        <div className="animated fadeIn">
            <Card>
                <CardHeader className="py-3">
                    <h3 className="mb-0">
                        <span className="text-muted me-1">#</span>
                        <strong>{roomName}</strong>
                        {roomDescription && (
                            <small className="ms-3 fw-normal" style={{ fontSize: '0.9rem' }}>
                                {roomDescription}
                            </small>
                        )}
                    </h3>
                </CardHeader>
                <CardBody>
                    {departmentId && (
                        <div className="d-flex align-items-center mb-3 gap-2">
                            <DepartmentAvatar
                                departmentId={departmentId}
                                width={45}
                                height={45}
                                showGuestBadge={false}
                            />
                            {departmentName && (
                                <h3 className="mb-0"><strong>{departmentName}</strong></h3>
                            )}
                        </div>
                    )}

                    <Text color="muted" className="mb-4 d-block">
                        To collaborate with this agency, you must review and accept the following terms.
                    </Text>

                    {agreementName && (
                        <h5 className="fw-bold mb-2">{agreementName}</h5>
                    )}

                    <AgreementTextEditor
                        value={content}
                        editable={false}
                        maxHeight={340}
                        minHeight={100}
                        onReachedBottom={() => setReadToEnd(true)}
                        footer={(effectiveDate || version != null) && (
                            <div className="pb-3">
                                <hr />
                                {effectiveDate && (
                                    <small className="d-block">
                                        Effective Date: {moment(effectiveDate).format('MM/DD/YYYY')}
                                    </small>
                                )}
                                {version != null && (
                                    <small className="d-block">Version {version}</small>
                                )}
                            </div>
                        )}
                    />

                    <div className="d-flex align-items-center mt-4">
                        <Tooltip placement="top">
                            <TooltipTrigger asChild>
                                {/* When disabled, the checkbox drops out of the tab order, so make the
                                    wrapper focusable (tabIndex 0) — this keeps the "read to the end"
                                    tooltip reachable by keyboard, since it opens on focus. */}
                                <span
                                    style={{ cursor: !readToEnd ? 'not-allowed' : 'default' }}
                                    tabIndex={!readToEnd ? 0 : undefined}
                                    role={!readToEnd ? 'button' : undefined}
                                    aria-disabled={!readToEnd ? true : undefined}
                                    aria-label={!readToEnd ? 'I have read and agree to the terms of this document. Read the entire document to the end before agreeing.' : undefined}
                                >
                                    <span style={{ pointerEvents: !readToEnd ? 'none' : undefined }} className="d-inline-flex">
                                        <Checkbox
                                            name="agreed"
                                            label="I have read and agree to the terms of this document."
                                            checked={isAgreed}
                                            onChange={({ checked }) => setIsAgreed(checked)}
                                            disabled={!readToEnd}
                                        />
                                    </span>
                                </span>
                            </TooltipTrigger>
                            {!readToEnd && (
                                <TooltipContent>
                                    Please read the entire document to the end before agreeing.
                                </TooltipContent>
                            )}
                        </Tooltip>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        {showSteps ? (
                            <small className="text-muted">Step {currentStep} of {totalSteps}</small>
                        ) : (
                            <span />
                        )}
                        <div>
                            {onBack && (
                                <Button
                                    color="secondary"
                                    className="me-2"
                                    onClick={onBack}
                                >
                                    Back
                                </Button>
                            )}
                            <Button
                                color="primary"
                                disabled={!isAgreed || isAccepting}
                                loading={isAccepting}
                                onClick={onAccept}
                            >
                                Continue
                            </Button>
                        </div>
                    </div>
                </CardBody>
            </Card>
        </div>
    )
})

AgreementGate.displayName = 'AgreementGate'
export { AgreementGate }
